import { NavLink } from "react-router-dom";
import { LayoutDashboard, MessageSquare, Globe, Menu } from "lucide-react";
import { useBriefingActionCount } from "@/hooks/use-briefing-action-count";
import { useNeedsZoneRefine } from "@/hooks/use-needs-zone-refine";
import { useIsMobile } from "@/hooks/use-mobile";
import { useSidebar } from "@/components/ui/sidebar";
import { JulietteOrb } from "@/components/juliette/JulietteOrb";
import { GuidanceNudgeBubble } from "@/components/juliette/GuidanceNudgeBubble";
import { useJulietteLauncher } from "@/hooks/use-juliette-launcher";
import { useJulietteActiveSession } from "@/hooks/use-juliette-active-session";
import { cn } from "@/lib/utils";

// ─── Items de la barre principale ─────────────────────────────────────────
//  Position 1 : Accueil
//  Position 2 : Messagerie
//  Position 3 : [Orb Juliette — bouton central surélevé, signature visuelle V3.5]
//  Position 4 : Sniper Web
//  Position 5 : Menu (ouvre la sidebar avec toutes les autres pages, dont Carnet Terrain)

type NavItem = {
  url: string;
  label: string;
  icon: typeof LayoutDashboard;
  end: boolean;
  badge?: "messages";
};

const LEFT_ITEMS: NavItem[] = [
  { url: "/", label: "Accueil", icon: LayoutDashboard, end: true },
  { url: "/messages", label: "Messages", icon: MessageSquare, end: false, badge: "messages" },
];

const RIGHT_ITEMS: NavItem[] = [
  { url: "/scraping", label: "Sniper", icon: Globe, end: false },
];

// ─── Composant ──────────────────────────────────────────────────────────────

export function MobileBottomNav() {
  const isMobile = useIsMobile();
  const { setOpenMobile } = useSidebar();
  const { open: openJuliette } = useJulietteLauncher();
  const { data: activeSession } = useJulietteActiveSession();
  const { data: briefingCount = 0 } = useBriefingActionCount();
  const needsZoneRefine = useNeedsZoneRefine();

  if (!isMobile) return null;

  const hasPendingSession =
    activeSession?.status === "pending" || activeSession?.status === "notified";

  return (
    <nav
      className={cn(
        "fixed bottom-0 left-0 right-0 z-40",
        "border-t border-border bg-card/95 backdrop-blur-lg",
        "pb-[env(safe-area-inset-bottom)] md:hidden",
      )}
      aria-label="Navigation principale"
    >
      <ul className="grid grid-cols-5 h-16">
        {/* ─── 1 & 2 : Accueil + Messagerie ─── */}
        {LEFT_ITEMS.map((item) => (
          <li key={item.url}>
            <NavTab item={item} briefingCount={briefingCount} needsZoneRefine={needsZoneRefine} />
          </li>
        ))}

        {/* ─── 3 : Orb Juliette (bouton central surélevé) ─── */}
        <li className="relative">
          {/* Coup de pouce d'accompagnement : émane de l'orbe, au-dessus de la nav. */}
          <GuidanceNudgeBubble placement="mobile" />
          <div className="absolute left-1/2 -translate-x-1/2 -top-5 rounded-full border-4 border-card shadow-lg shadow-indigo-500/30">
            <JulietteOrb
              size="lg"
              state="idle"
              onActivate={() => openJuliette()}
              hasPendingSession={hasPendingSession}
              ariaLabel={
                hasPendingSession
                  ? "Juliette a une session pour toi"
                  : "Ouvrir Juliette"
              }
            />
          </div>
        </li>

        {/* ─── 4 : Sniper ─── */}
        {RIGHT_ITEMS.map((item) => (
          <li key={item.url}>
            <NavTab item={item} briefingCount={briefingCount} needsZoneRefine={needsZoneRefine} />
          </li>
        ))}

        {/* ─── 5 : Menu (ouvre la sidebar — contient Carnet Terrain) ─── */}
        <li>
          <button
            type="button"
            onClick={() => setOpenMobile(true)}
            aria-label="Ouvrir le menu principal"
            className={cn(
              "h-full w-full flex flex-col items-center justify-center gap-0.5",
              "text-3xs font-medium transition-colors",
              "text-muted-foreground hover:text-foreground",
            )}
          >
            <Menu className="h-5 w-5" />
            <span className="leading-none">Menu</span>
          </button>
        </li>
      </ul>
    </nav>
  );
}

// ─── Sous-composant : onglet standard ─────────────────────────────────────

function NavTab({
  item,
  briefingCount,
  needsZoneRefine,
}: {
  item: NavItem;
  briefingCount: number;
  needsZoneRefine: boolean;
}) {
  const showBadge = item.badge === "messages" && briefingCount > 0;
  // Sniper Web : pastille de rappel + deep-link sur l'onglet zones tant que
  // la zone de prospection n'est pas affinée.
  const showZoneRefine = item.url === "/scraping" && needsZoneRefine;
  const to = showZoneRefine ? "/scraping?tab=zones" : item.url;

  return (
    <NavLink
      to={to}
      end={item.end}
      className={({ isActive }) =>
        cn(
          "h-full w-full flex flex-col items-center justify-center gap-0.5 relative",
          "text-3xs font-medium transition-colors",
          isActive
            ? "text-primary"
            : "text-muted-foreground hover:text-foreground",
        )
      }
    >
      {({ isActive }) => (
        <>
          {isActive && (
            <span className="absolute top-0 left-1/2 -translate-x-1/2 h-0.5 w-8 bg-primary rounded-b-full" />
          )}
          <div className="relative">
            <item.icon className="h-5 w-5" />
            {showBadge && (
              <span className="absolute -top-1.5 -right-1.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-destructive px-1 text-4xs font-bold text-destructive-foreground">
                {briefingCount > 99 ? "99+" : briefingCount}
              </span>
            )}
            {showZoneRefine && (
              <span
                aria-label="Zone de prospection à affiner"
                className="absolute -top-1 -right-1 h-2.5 w-2.5 rounded-full bg-warning border-2 border-card"
              />
            )}
          </div>
          <span className="leading-none">{item.label}</span>
        </>
      )}
    </NavLink>
  );
}
